import PropTypes from 'prop-types';
import { useState } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { Controller, useFormContext } from 'react-hook-form';
import {
  TextField, Popper, List, ListItem, ListItemButton, ListItemText, Paper, Typography
} from '@mui/material';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
// config
import { GOOGLE_MAP_API } from '../../config';

const libraries = ['places'];

RHFPlaceAutoComplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  showMap: PropTypes.bool,
  draggablePin: PropTypes.bool,
};

export default function RHFPlaceAutoComplete({ name, label = 'Buscar...', showMap = false, draggablePin = false, center={lat: 0, lng: 0},  ...props }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API,
    libraries,
  });

  if (!isLoaded) return <div>Cargando mapa...</div>;

  return <PlaceAutoComplete name={name} label={label} showMap={showMap} draggablePin={draggablePin} center={center} {...props} />;
}

PlaceAutoComplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  showMap: PropTypes.bool,
  draggablePin: PropTypes.bool,
};

function PlaceAutoComplete({ name, label, showMap, draggablePin, center }) {
  const { control } = useFormContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(center);

  const {
    ready,
    value: inputValue,
    setValue: setInputValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'pr' }, // Limita a Puerto Rico
      types: ['address'], // Opcional: solo direcciones
    },
  });

  const handleInput = (e) => {
    setInputValue(e.target.value);
    setAnchorEl(e.currentTarget);
  };

  const handleSelect = async (description, onChange) => {
    setInputValue(description, false);
    clearSuggestions();
    setAnchorEl(null);

    try {
      const results = await getGeocode({ address: description });
      const { lat, lng } = await getLatLng(results[0]);

      const addressComponents = results[0].address_components;

      const getComponent = (type) => {
        const comp = addressComponents.find(c => c.types.includes(type));
        return comp ? comp.long_name : '';
      };

      const locationData = {
        address: description,
        latitude: lat,
        longitude: lng,
        city: getComponent('locality') || getComponent('administrative_area_level_2'),
        zip_code: getComponent('postal_code'),
      };
      
      setSelected({ lat, lng });
      onChange(locationData);
    } catch (error) {
      console.error('Error al obtener coordenadas:', error);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        
        const handleMarkerDragEnd = async (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
        
          try {
            const updatedData = {
              ...value,
              latitude: lat,
              longitude: lng,
              // address: '', 
            };
        
            setSelected({ lat, lng });
            onChange(updatedData);
          } catch (error) {
            console.error('Error al obtener coordenadas desde el pin:', error);
          }
        };        

        return (
          <div>
            <TextField
              fullWidth
              value={inputValue || value?.address || ''}
              onChange={handleInput}
              disabled={!ready}
              label={label}
              error={!!error}
              helperText={error?.message}
              autoComplete="off"
            />

            <Popper open={status === 'OK'} anchorEl={anchorEl} style={{ zIndex: 9999 }}>
              <Paper style={{ width: anchorEl?.clientWidth }}>
                <List>
                  {data.map(({ place_id, description }) => (
                    <ListItem key={place_id} disablePadding>
                      <ListItemButton onClick={() => handleSelect(description, onChange)}>
                        <ListItemText primary={description} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popper>

            {showMap && selected.lat > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ height: 300 }}>
                  <GoogleMap
                    zoom={15}
                    center={selected}
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                  >
                    <Marker
                      position={selected}
                      draggable={draggablePin}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  </GoogleMap>
                </div>
                <Typography variant="caption" sx={{ mt: 1 }}>
                  Puedes mover el pin para dar una mayor precisión.
                </Typography>
                {/* <Typography variant="body2" sx={{ mt: 1 }}>
                  Latitud: {selected.lat}, Longitud: {selected.lng}
                </Typography> */}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}
